// src/components/Login.js
import React, { useState, useEffect } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Link,
} from "@mui/material";
import { keyframes } from "@mui/system";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import DocumentPreview from "../../images/Document-Preview.jpeg";
import { red } from "@mui/material/colors";
import PasswordResetDialog from "./auth_component/PasswordResetDialog";

const fadeInSlide = keyframes`
  0% {
    opacity: 0;
    transform: translateX(50px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    setAnimate(true);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      const userDocRef = doc(db, "users", user.uid); // gets the document references
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        console.log("UID:", userData.uid);
        console.log("Role from Firestore:", userData.role);
        if (userData.role === "admin") {
          navigate("/admin-dashboard");
        } else {
          navigate("/dashboard");
        }
      } else {
        setError("No user data found in Firestore for this account.");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOpenResetDialog = () => {
    setResetDialogOpen(true);
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          width: "100vw",
          height: 65,
          backgroundImage:
            "linear-gradient(to right, rgba(32, 32, 32, 100), rgba(32, 32, 32, 0.98))",
          display: "flex",
          alignItems: "center",
          justifyContent: "start",
          paddingLeft: 5,
          color: "white",
          fontWeight: "bold",
          fontSize: 24,
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 1000,
          backdropFilter: "blur(4px)", // Optional: adds a nice glass effect
          letterSpacing: "0.5px", // Optional: crisper typography
        }}
      >
        Detect-Classify-Review
      </Box>
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          width: "100vw",
          fontFamily: "'Roboto', sans-serif",
          paddingTop: 0,
          paddingBottom: 10,
          overflowY: "hidden",
          overflowX: "hidden",
          flexDirection: "row",
          flexWrap: "nowrap",
          alignItems: "stretch",
          boxSizing: "border-box",
        }}
      >
        {/* Left side - Login Form */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            paddingRight: 6,
            paddingTop: 20,
            backgroundColor: "#FAF9F6",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
            animation: animate ? `${fadeInSlide} 1.2s ease forwards` : "none",
            height: "100vh",
            overflow: "hidden",
            flexDirection: "row",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 400,
              paddingTop: 0,
              zIndex: 5,
              paddingRight: 1000,
              backgroundColor: "white",
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              padding: 4,
              height: "60vh",
              overflowY: "hidden",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <AccountCircleIcon sx={{ fontSize: 80, color: "#4a90e2" }} />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ textAlign: "center", fontWeight: "bold" }}
            >
              Login
            </Typography>
            {error && (
              <Typography color="error" sx={{ mt: 1, textAlign: "center" }}>
                {error}
              </Typography>
            )}
            <Box
              component="form"
              onSubmit={handleLogin}
              sx={{ mt: 0, width: "100%" }}
            >
              <TextField
                label="Email"
                variant="filled"
                fullWidth
                margin="normal"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "#7a7a7a" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  borderRadius: 1,
                  "& .MuiFilledInput-root": {
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.85)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                    },
                  },
                }}
              />
              <TextField
                label="Password"
                variant="filled"
                fullWidth
                margin="normal"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "#7a7a7a" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  borderRadius: 1,
                  "& .MuiFilledInput-root": {
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.85)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                type="submit"
                fullWidth
                sx={{
                  mt: 3,
                  backgroundColor: "#4a90e2",
                  "&:hover": {
                    backgroundColor: "#357ABD",
                    boxShadow: "0 0 12px #357ABD",
                  },
                  transition: "all 0.3s ease",
                  fontWeight: "bold",
                }}
              >
                Login
              </Button>
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Link
                  href="#"
                  underline="hover"
                  sx={{
                    justifyContent: "start",
                    color: "#1976d2",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenResetDialog();
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right side - About Me with background image and overlay */}
        <Box
          sx={{
            flex: 1.2,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: 6,
            marginLeft: -20,
            fontSize: 18,
            lineHeight: 1.6,
            color: "white",
            borderRadius: 3,
            backgroundImage: `linear-gradient(rgba(25, 118, 210, 0.7), rgba(12, 52, 92, 0.7)), url(${DocumentPreview})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            height: "100vh",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: 200,
              height: "100%",
              borderTopLeftRadius: 3,
              borderBottomLeftRadius: 3,
              pointerEvents: "none",
              background:
                "linear-gradient(to right, rgba(51, 120, 189, 0.7), transparent)",
              zIndex: 1,
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              overflowY: "hidden",
              height: "100%",
              paddingTop: 30,
              boxSizing: "border-box",
            }}
          >
            <Typography variant="h3" component="h2" gutterBottomssx={{}}>
              Transforming Document Management Through Intelligent Analysis
            </Typography>
            <Typography
              variant="h5"
              sx={{
                paddingTop: 3,
                textTransform: "uppercase",
                fontWeight: 200, // optional
              }}
            >
              Organize accreditation documents with intelligent, web-based
              classification and analysis.
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        component="footer"
        sx={{
          width: "100vw",
          height: 120,
          backgroundImage:
            "linear-gradient(to bottom, #F7ECD7,rgb(237, 227, 208))",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#333",
          fontWeight: "medium",
          fontSize: 16,
          position: "fixed",
          bottom: 0,
          left: 0,
          zIndex: 1000,
          padding: 3,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            width: "100%",
            mt: 1,
            flexWrap: "wrap",
            color: "#000",
          }}
        >
          <Box
            sx={{
              flex: "1 1 100px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              marginLeft: 10,
              marginRight: 10,
              px: 3,
              opacity: 0,
              animation: "fadeIn 1s ease-in-out forwards",
              animationDelay: "0.3s",
              "@keyframes fadeIn": {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
            }}
          >
            <AccountCircleIcon sx={{ fontSize: 40, color: "#4a90e2" }} />
            <Box
              sx={{
                fontWeight: "bold",
                fontSize: 15,
                color: "#4a90e2",
                paddingTop: 0,
              }}
            >
              Modern and scalable
            </Box>
            <Box sx={{ fontSize: 13, color: "#000" }}>
              Built with React for a smooth user experience and<br></br>
              Firebase for secure, real-time data management.
            </Box>
          </Box>
          <Box
            sx={{
              flex: "1 1 100px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              marginLeft: 10,
              marginRight: 10,
              px: 3,
              opacity: 0,
              animation: "fadeIn 1s ease-in-out forwards",
              animationDelay: "0.6s",
              "@keyframes fadeIn": {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
            }}
          >
            <LockIcon sx={{ fontSize: 40, color: "#4a90e2" }} />
            <Box
              sx={{
                fontWeight: "bold",
                fontSize: 15,
                color: "#4a90e2",
              }}
            >
              Smart AI with NLP & Visual Learning
            </Box>
            <Box sx={{ fontSize: 13, color: "#000" }}>
              Utilizes Natural Language Processing and Vision-Language Models to
              classify, and organize documents intelligently.
            </Box>
          </Box>
          <Box
            sx={{
              flex: "1 1 100px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              marginLeft: 10,
              marginRight: 10,
              opacity: 0,
              animation: "fadeIn 1s ease-in-out forwards",
              animationDelay: "0.9s",
              "@keyframes fadeIn": {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
            }}
          >
            <AccountCircleIcon sx={{ fontSize: 40, color: "#4a90e2" }} />
            <Box sx={{ fontWeight: "bold", fontSize: 15, color: "#4a90e2" }}>
              Easy to Use
            </Box>
            <Box sx={{ fontSize: 13, color: "#000" }}>
              Ready to go with your content — simple to navigate, easy to
              customize, and designed with usability in mind.
            </Box>
          </Box>
        </Box>
      </Box>
      <PasswordResetDialog
        open={resetDialogOpen}
        onClose={handleCloseResetDialog}
      />
    </>
  );
}

export default Login;
